import React, { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { presentMutationError, toastError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_FIELD_DESC_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";
import SettingCard from "./SettingCard";
import { deleteAllTrades } from "../../../../services/tradeApi";
import { notifyTradesUpdated } from "../../../../hooks/useTradeStatistics";

const ResetTradeDataCard: React.FC = () => {
  const { user } = useUser();
  const [isClearing, setIsClearing] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  const handleClearAllTrades = async () => {
    if (!user?.id) {
      toastError("Authentication required", { description: "Please log in to clear trades" });
      return;
    }

    setIsClearing(true);
    setIsClearModalOpen(false);

    try {
      await deleteAllTrades(undefined);

      notifyTradesUpdated({ userId: user.id, action: "clear" });

      toast.success("All trades cleared", {
        description: "Your trade log has been reset. You can now sync fresh data from your broker.",
      });
    } catch (error: unknown) {
      presentMutationError(error, "Error");
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <>
      <SettingCard
        title="Reset Trade Data"
        description="Clear all existing trades to start fresh before syncing broker data."
      >
        <div className="flex flex-col gap-4">
          <Alert variant="warning" className={BROKER_ALERT_CLASS}>
            <BrokerAlertIcon tone="warning" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              This will permanently delete all trades in your trade log. This action cannot be undone.
            </AlertDescription>
          </Alert>
          <Button
            type="button"
            variant="gradient-red"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={isClearing}
            aria-busy={isClearing || undefined}
            onClick={() => setIsClearModalOpen(true)}
          >
            {isClearing ? (
              <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
            ) : null}
            Clear all trades
          </Button>
        </div>
      </SettingCard>

      <StrategyPineDetailDialog
        open={isClearModalOpen}
        onOpenChange={(open) => {
          if (!open && !isClearing) setIsClearModalOpen(false);
        }}
        title="Clear All Trades?"
        showHeader={false}
        showDismissButton={false}
        contentClassName="sm:max-w-md"
        bodyClassName="flex-none overflow-visible"
        footer={
          <DashboardLayoutDialogFooter onCancel={() => setIsClearModalOpen(false)}>
            <Button
              type="button"
              variant="gradient-red"
              size="sm"
              data-font="primary"
              disabled={isClearing}
              aria-busy={isClearing || undefined}
              onClick={() => void handleClearAllTrades()}
            >
              Clear all trades
            </Button>
          </DashboardLayoutDialogFooter>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 font-[family-name:var(--font-primary)] text-sm text-gray-700 dark:text-gray-200">
            Are you sure you want to delete all trades? This action cannot be undone.
          </p>
          <p className={BROKER_FIELD_DESC_CLASS}>
            This will permanently remove all trades from your trade log. You can sync new trades from
            your broker after clearing.
          </p>
        </div>
      </StrategyPineDetailDialog>
    </>
  );
};

export default ResetTradeDataCard;
